import { useState, useCallback, useEffect } from 'react'
import { Toast, Modal, TextField, Text, Box, Image, Avatar, VerticalStack, HorizontalStack, Button, HorizontalGrid, Divider, AlphaCard, Icon } from '@shopify/polaris'
import { updateSocials } from '../firebase'
import { ImageMajor, PlayCircleMajor } from '@shopify/polaris-icons'

import Tiktok from "../assets/Tiktok.svg";

export function OnboardingSocialForm({ profileSocials, socials, setSocials }) {
  return (
    <HorizontalGrid columns={{ xs: '1' }} gap="4">
      <AlphaCard>
        <VerticalStack gap="2">
          <HorizontalStack align="space-between" blockAlign="center">
            <Text variant="headingMd" color="warning">Instagram</Text>
            <Box>
              <Icon source={ImageMajor} color="warning" />
            </Box>
          </HorizontalStack>
          <Divider />
          {
            profileSocials?.instagram?.url ? (
              <>
                <HorizontalStack gap="2" blockAlign="center">
                  <Avatar shape="square" size="large" source={profileSocials.instagram?.stats?.avatar} />
                  <Box>
                    <Text variant="bodyLg">@{profileSocials?.instagram?.stats?.atName}</Text>
                    <Text variant="bodySm">{profileSocials?.instagram?.stats?.followers?.toLocaleString()} Followers</Text>
                  </Box>
                </HorizontalStack>
              </>
            ) : (
              <TextField
                value={socials.instagram?.url || ''}
                onChange={(v) => {
                  setSocials({
                    ...socials,
                    instagram: {
                      url: v
                    }
                  })
                }
                }
                placeholder="instagram.com/..."
                autoComplete="off"
                helpText="Your Instagram URL"
              />
            )
          }
        </VerticalStack>
      </AlphaCard>
      <AlphaCard>
        <VerticalStack gap="2">
          <HorizontalStack align="space-between" blockAlign="center">
            <Text variant="headingMd">TikTok</Text>
            <Box>
              <Image source={Tiktok} width="20" />
            </Box>
          </HorizontalStack>
          <Divider />
          {
            (profileSocials?.tiktok?.url) ? (
              <>
                <HorizontalStack gap="2" blockAlign="center">
                  <Avatar shape="square" size="large" source={profileSocials.tiktok?.stats?.avatar} />
                  <Box>
                    <Text variant="bodyLg">@{profileSocials?.tiktok?.stats?.atName}</Text>
                    <Text variant="bodySm">{profileSocials?.tiktok?.stats?.followers?.toLocaleString()} Followers</Text>
                  </Box>
                </HorizontalStack>
              </>
            ) : (
              <TextField
                value={profileSocials.tiktok?.url || ''}
                onChange={(v) => {
                  setSocials({
                    ...socials,
                    tiktok: {
                      url: v
                    }
                  })
                }
                }
                placeholder="https://tiktok.com/..."
                autoComplete="off"
                helpText="Your Tiktok URL"
              />
            )
          }
        </VerticalStack>
      </AlphaCard>
      <AlphaCard>
        <VerticalStack gap="2">
          <HorizontalStack align="space-between" blockAlign="center">
            <Text variant="headingMd" color="critical">YouTube</Text>
            <Box>
              <Icon source={PlayCircleMajor} color="critical" />
            </Box>
          </HorizontalStack>
          <Divider />
          {
            profileSocials?.youtube?.url ? (
              <>
                <HorizontalStack gap="2" blockAlign="center">
                  <Avatar shape="square" size="large" source={profileSocials?.youtube?.stats?.avatar} />
                  <Box>
                    <Text variant="bodyLg">{profileSocials?.youtube?.stats?.name}</Text>
                    <Text variant="bodySm">{profileSocials?.youtube?.stats?.followers?.toLocaleString()} Subscribers</Text>
                  </Box>
                </HorizontalStack>
              </>
            ) : (
              <TextField
                value={socials.youtube?.url || ''}
                onChange={(v) => {
                  setSocials({
                    ...socials,
                    youtube: {
                      url: v
                    }
                  })
                }
                }
                placeholder="youtube.com/..."
                autoComplete="off"
                helpText="Your Youtube URL"
              />
            )
          }
        </VerticalStack>
      </AlphaCard>
    </HorizontalGrid>
  )
}

export default function SocialMediaModal({ profile, open, onClose }) {

  const { socials } = profile
  const [localSocials, setLocalSocials] = useState(socials)
  const [refreshResponse, setRefreshResponse] = useState([])

  const [toast, setToast] = useState(null);
  const toggleActive = useCallback(() => setToast(null), []);
  const toastMarkup = toast ? (
    <Toast content={toast.content} error={toast.isError} onDismiss={toggleActive} />
  ) : null;

  useEffect(() => {
    setLocalSocials(socials)
  }, [socials])

  const [loading, setLoading] = useState(false)

  async function saveSocialMedia() {
    try {
      setLoading(true);
      setRefreshResponse([])
      const { data: responses } = await updateSocials(localSocials)
      setRefreshResponse(responses)
    } catch (e) {
      console.error(e)
      setToast({
        content: 'Failed... Please try again.',
        isError: true
      })
    } finally {
      setLoading(false)
    }
  }

  const noChanges = localSocials.instagram?.url === socials.instagram?.url && localSocials.tiktok?.url === socials.tiktok?.url && localSocials.youtube?.url === socials.youtube?.url

  function getPlatformResponseMarkup(platform) {
    const response = refreshResponse?.find(r => r.platform === platform)
    if (!response) return null
    if (response.success === false) {
      return (
        <Text color="critical" variant="bodySm">{response.error}</Text>
      )
    } else if (response.success === true) {
      return (
        <Text color="success" variant="bodySm">Success</Text>
      )
    } else {
      return null
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        setLocalSocials(socials)
        onClose()
      }}
      title={'Social Media'}
      primaryAction={{
        content: noChanges ? 'Done' : 'Update',
        loading: loading,
        onAction: noChanges ? onClose : saveSocialMedia
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: () => {
            setLocalSocials(socials)
            onClose()
          }
        }
      ]}
    >
      <Modal.Section>
        {toastMarkup}
        <HorizontalGrid columns={{ xs: '1', md: '2' }} gap="4">
          <AlphaCard>
            <VerticalStack gap="2">
              <HorizontalStack align="space-between" blockAlign="center">
                <Text variant="headingMd" color="warning">Instagram</Text>
                <Box>
                  <Icon source={ImageMajor} color="warning" />
                </Box>
              </HorizontalStack>
              <Divider />
              {
                socials?.instagram?.url && (localSocials.instagram?.url === socials.instagram?.url) ? (
                  <>
                    <HorizontalStack gap="2" blockAlign="center">
                      <Avatar shape="square" size="large" source={localSocials.instagram?.stats?.avatar} />
                      <Box>
                        <Text variant="bodyLg">@{localSocials?.instagram?.stats?.atName}</Text>
                        <Text variant="bodySm">{localSocials?.instagram?.stats?.followers?.toLocaleString()} Followers</Text>
                      </Box>
                    </HorizontalStack>
                    <Button onClick={() => setLocalSocials({
                      ...localSocials,
                      instagram: { url: '' }
                    })} plain>Remove</Button>
                  </>
                ) : (
                  <TextField
                    value={localSocials.instagram?.url || ''}
                    onChange={(v) => {
                      setLocalSocials({
                        ...localSocials,
                        instagram: {
                          url: v
                        }
                      })
                    }
                    }
                    placeholder="instagram.com/..."
                    autoComplete="off"
                    helpText="Your Instagram URL"
                  />
                )
              }
              {
                getPlatformResponseMarkup('instagram')
              }
            </VerticalStack>
          </AlphaCard>
          <AlphaCard>
            <VerticalStack gap="2">
              <HorizontalStack align="space-between" blockAlign="center">
                <Text variant="headingMd">TikTok</Text>
                <Box>
                  <Image source={Tiktok} width="20" />
                </Box>
              </HorizontalStack>
              <Divider />
              {
                socials.tiktok && (localSocials.tiktok?.url === socials.tiktok?.url) ? (
                  <>
                    <HorizontalStack gap="2" blockAlign="center">
                      <Avatar shape="square" size="large" source={localSocials.tiktok?.stats?.avatar} />
                      <Box>
                        <Text variant="bodyLg">@{localSocials?.tiktok?.stats?.atName}</Text>
                        <Text variant="bodySm">{localSocials?.tiktok?.stats?.followers?.toLocaleString()} Followers</Text>
                      </Box>
                    </HorizontalStack>
                    <Button onClick={() => setLocalSocials({
                      ...localSocials,
                      tiktok: { url: '' }
                    })} plain>Remove</Button>
                  </>
                ) : (
                  <TextField
                    value={localSocials.tiktok?.url || ''}
                    onChange={(v) => {
                      setLocalSocials({
                        ...localSocials,
                        tiktok: {
                          url: v
                        }
                      })
                    }
                    }
                    placeholder="https://tiktok.com/..."
                    autoComplete="off"
                    helpText="Your Tiktok URL"
                  />
                )
              }
              {
                getPlatformResponseMarkup('tiktok')
              }
            </VerticalStack>
          </AlphaCard>
          <AlphaCard>
            <VerticalStack gap="2">
              <HorizontalStack align="space-between" blockAlign="center">
                <Text variant="headingMd" color="critical">YouTube</Text>
                <Box>
                  <Icon source={PlayCircleMajor} color="critical" />
                </Box>
              </HorizontalStack>
              <Divider />
              {
                socials.youtube && (localSocials.youtube?.url === socials.youtube?.url) ? (
                  <>
                    <HorizontalStack gap="2" blockAlign="center">
                      <Avatar shape="square" size="large" source={localSocials?.youtube?.stats?.avatar} />
                      <Box>
                        <Text variant="bodyLg">{localSocials?.youtube?.stats?.name}</Text>
                        <Text variant="bodySm">{localSocials?.youtube?.stats?.followers?.toLocaleString()} Subscribers</Text>
                      </Box>
                    </HorizontalStack>
                    <Button onClick={() => setLocalSocials({
                      ...localSocials,
                      youtube: { url: '' }
                    })} plain>Remove</Button>
                  </>
                ) : (
                  <TextField
                    value={localSocials.youtube?.url || ''}
                    onChange={(v) => {
                      setLocalSocials({
                        ...localSocials,
                        youtube: {
                          url: v
                        }
                      })
                    }
                    }
                    placeholder="youtube.com/..."
                    autoComplete="off"
                    helpText="Your Youtube URL"
                  />
                )
              }
              {
                getPlatformResponseMarkup('youtube')
              }
            </VerticalStack>
          </AlphaCard>
        </HorizontalGrid>
      </Modal.Section>
    </ Modal>
  )
}
